import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  Grid,
  Alert,
  CircularProgress,
  Chip,
  List,
  ListItem,
  ListItemText,
  Divider,
  Paper,
  Autocomplete,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";
import {
  CheckCircle,
  Error,
  Warning,
  ArrowBack,
  ArrowForward,
  Star,
  Delete,
  Add,
} from "@mui/icons-material";
import { usePreviewTimetable, useGenerateTimetable, useCreateTimetableFromScratch } from "../../../hooks/queries/useTimetable";
import { useAllDepartments } from "../../../hooks/queries/useDepartments";
import { useClasses, useSubjects } from "../../../hooks/queries/useCourses";
import { useSubjectsByClass } from "../../../hooks/queries/useClassSubject";
import { useInstitutes } from "../../../hooks/queries/useInstitutes";
import { useAcademicYears } from "../../../hooks/queries/useAcademicYears";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Snackbar } from "@mui/material";
import { validateRequired } from "../../../utils/validation";
import useEffectiveRolePath, { normalizeRoleSlug } from "../../../hooks/useEffectiveRolePath";

const getSubjectOptionId = (subject) => {
  const id = subject?.id ?? subject?.subjectId;
  const n = Number(id);
  return Number.isFinite(n) && n > 0 ? n : null;
};

const buildSubjectIdsPayload = (subjectIds) => {
  const ids = (Array.isArray(subjectIds) ? subjectIds : [])
    .map((id) => Number(id))
    .filter((id) => Number.isFinite(id) && id > 0);
  return ids.length > 0 ? { subjectIds: ids } : {};
};

/** Unwrap axios + API envelope: { status, message, data: { ... } } → inner payload */
function unwrapApiPayload(response) {
  const body = response?.data;
  if (!body) return null;
  if (body.data != null && typeof body.data === "object") return body.data;
  return body;
}

function getActionableTimetableWarnings(report) {
  return (report?.warnings || []).filter((warning) => warning?.type === "tight_schedule");
}

const GenerationWizard = () => {
  const navigate = useNavigate();
  const { rolePath } = useEffectiveRolePath();
  const user = useSelector((state) => state.auth?.user);
  const selectedInstitute = useSelector((state) => state.instituteContext?.selectedInstitute);
  const [activeStep, setActiveStep] = useState(0);
  
  // Get current user from localStorage
  const [currentUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  });

  // Canonical, case-insensitive role slug. The stored role can be "superadmin",
  // "SuperAdmin", "super_admin", "institute", etc. normalizeRoleSlug lowercases +
  // strips separators and maps institute→admin, superadministrator→superadmin.
  // Gates below MUST use this, never the raw currentUser.role (case-sensitive bug).
  const normalizedRole = normalizeRoleSlug(currentUser?.role);
  const isSuperAdminRole = normalizedRole === "superadmin";
  
  const [formData, setFormData] = useState({
    generationMode: "fully_automatic",
    academicYear: `${new Date().getFullYear()}-${new Date().getFullYear() + 1}`,
    instituteId: currentUser?.instituteId || null,
    departmentId: currentUser?.departmentId || null,
    classId: null,
    subjectIds: [],
    excludeWeekends: true,
    excludeHolidays: [],
    scheduleConfig: {
      dayStart: "09:00",
      dayEnd: "22:00",
      slotDurationMinutes: 60,
      breaks: [{ start: "12:00", end: "13:00", label: "Lunch" }],
    },
  });
  const [previewResults, setPreviewResults] = useState(null);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [generatedTimetableId, setGeneratedTimetableId] = useState(null);
  const [academicYearError, setAcademicYearError] = useState(null);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [generationError, setGenerationError] = useState(null);
  
  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success', // 'success', 'error', 'warning', 'info'
  });

  const resolveWizardInstituteId = React.useCallback(
    () =>
      formData.instituteId ||
      currentUser?.instituteId ||
      user?.instituteId ||
      selectedInstitute?.id ||
      null,
    [formData.instituteId, currentUser?.instituteId, user?.instituteId, selectedInstitute?.id]
  );

  const previewMutation = usePreviewTimetable();
  const generateMutation = useGenerateTimetable();
  const createFromScratchMutation = useCreateTimetableFromScratch();

  const effectiveInstituteId =
    formData.instituteId || selectedInstitute?.id || currentUser?.instituteId || null;

  // Super admin in institute context: default institute from context
  React.useEffect(() => {
    if (selectedInstitute?.id != null && isSuperAdminRole) {
      setFormData((prev) => ({
        ...prev,
        instituteId: prev.instituteId ?? selectedInstitute.id,
      }));
    }
  }, [selectedInstitute?.id, currentUser?.role]);

  // Fetch data for dropdowns
  // Only fetch institutes if user is superAdmin
  const { data: institutesData, isError: institutesError, isLoading: institutesLoading } = useInstitutes({ limit: 99999 }, {
    enabled: isSuperAdminRole
  });
  // Only fetch departments if not HOD (HOD already has departmentId)
  // When superAdmin selects institute, filter departments by that institute
  const { data: departmentsData, isLoading: departmentsLoading } = useAllDepartments({
    limit: 9999,
    page: 1,
    ...(effectiveInstituteId && isSuperAdminRole && { instituteId: effectiveInstituteId }),
  }, {
    enabled: ['admin', 'superadmin'].includes(normalizedRole)
  });
  // Request classes - filter by department when selected
  const { data: classesData, isLoading: classesLoading } = useClasses({
    page: 1,
    limit: 'all',
    sort: 'name',
    order: 'ASC',
    ...(formData.departmentId && { departmentId: formData.departmentId }),
    ...(effectiveInstituteId && isSuperAdminRole && { instituteId: effectiveInstituteId }),
  });
  const { data: subjectsData, isLoading: subjectsLoading } = useSubjects();
  const { data: classSubjectsData, isLoading: classSubjectsLoading } = useSubjectsByClass(
    formData.classId,
    formData.academicYear ? { academicYear: formData.academicYear } : {}
  );
  const { data: academicYearsData, isLoading: academicYearsLoading } = useAcademicYears({
    ...(effectiveInstituteId && isSuperAdminRole && { instituteId: effectiveInstituteId }),
  });

  const institutes = institutesData?.data?.institutes || institutesData?.data || [];
  const departments = Array.isArray(departmentsData?.data) ? departmentsData.data : (departmentsData?.data?.departments || []);
  const classes = Array.isArray(classesData?.data) ? classesData.data : (classesData?.data?.classes || classesData?.data?.data || []);
  const subjects = subjectsData?.data?.data?.subjects || subjectsData?.data?.subjects || [];
  const academicYears = Array.isArray(academicYearsData) ? academicYearsData : [];

  // Cascading: classes are filtered by API when departmentId is passed; otherwise show all
  const filteredClasses = Array.isArray(classes) ? classes : [];
  
  const subjectsDropdownLoading = formData.classId ? classSubjectsLoading : subjectsLoading;

  // Filter subjects: when class selected, show only subjects assigned to that class; otherwise all subjects
  const filteredSubjects = React.useMemo(() => {
    if (!formData.classId) {
      return subjects; // No class selected - show all (for optional subject filter when generating for all)
    }
    if (classSubjectsLoading) {
      return [];
    }
    const classSubjects = classSubjectsData?.data?.data?.subjects || classSubjectsData?.data?.subjects || [];
    return Array.isArray(classSubjects) ? classSubjects : [];
  }, [subjects, formData.classId, classSubjectsData, classSubjectsLoading]);
  
  const showInstituteSelection = isSuperAdminRole;
  // HOD already has a department, only admin/institute/superAdmin can select departments.
  // normalizeRoleSlug maps institute→admin, so the set is ['admin','superadmin'].
  const showDepartmentSelection = ['admin', 'superadmin'].includes(normalizedRole);

  // Sync: clear department/class/subject when selected values are no longer in filtered lists
  React.useEffect(() => {
    if (formData.departmentId && showDepartmentSelection) {
      const deptExists = departments.some((d) => (d.id || d.departmentId) === formData.departmentId);
      if (!deptExists) {
        setFormData((prev) => ({ ...prev, departmentId: null, classId: null, subjectIds: [] }));
      }
    }
  }, [departments, formData.departmentId, showDepartmentSelection]);

  // Set first available academic year as default if none is selected
  React.useEffect(() => {
    if (!formData.academicYear && academicYears.length > 0 && !academicYearsLoading) {
      setFormData((prev) => ({ ...prev, academicYear: academicYears[0].academicYear }));
    }
  }, [academicYears, academicYearsLoading, formData.academicYear]);

  const steps =
    formData.generationMode === "fully_manual"
      ? ["Configure Parameters", "Create Empty Timetable"]
      : ["Configure Parameters", "Check Feasibility", "Generate Timetable"];

  React.useEffect(() => {
    if (activeStep >= steps.length) setActiveStep(Math.max(0, steps.length - 1));
  }, [formData.generationMode, steps.length]);

  const handleNext = () => {
    if (activeStep === 0) {
      const yearError = validateRequired(formData.academicYear, "Academic year");
      setAcademicYearError(yearError);
      if (yearError) {
        toast.error(yearError);
        return;
      }
      if (formData.generationMode === "fully_manual") {
        setActiveStep(1);
      } else {
        setPreviewResults(null);
        setActiveStep(1);
      }
    } else if (activeStep === 1) {
      if (formData.generationMode === "fully_manual") {
        handleCreateFromScratch();
      } else {
        handlePreview();
      }
    } else if (activeStep === 2) {
      handleGenerate();
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const buildScheduleConfigPayload = () => {
    const sc = formData.scheduleConfig || {};
    const breaks = Array.isArray(sc.breaks)
      ? sc.breaks
          .filter((b) => b && (b.start || b.end))
          .map((b) => ({
            start: String(b.start || "12:00").slice(0, 5),
            end: String(b.end || "13:00").slice(0, 5),
            label: (b.label && String(b.label).trim()) || "Break",
          }))
      : [];
    return {
      dayStart: String(sc.dayStart || "09:00").slice(0, 5),
      dayEnd: String(sc.dayEnd || "22:00").slice(0, 5),
      slotDurationMinutes: Math.max(1, Math.min(600, Number.parseInt(String(sc.slotDurationMinutes || 60), 10) || 60)),
      breaks: breaks.length > 0 ? breaks : [{ start: "12:00", end: "13:00", label: "Lunch" }],
    };
  };

  const handlePreview = async () => {
    const instituteId = resolveWizardInstituteId();
    if (!instituteId) {
      toast.error("Select an institute before checking feasibility.");
      return;
    }
    try {
      const result = await previewMutation.mutateAsync({
        academicYear: formData.academicYear,
        instituteId,
        departmentId: formData.departmentId,
        classIds: formData.classId ? [formData.classId] : undefined,
        ...buildSubjectIdsPayload(formData.subjectIds),
        excludeWeekends: formData.excludeWeekends,
        ...(Array.isArray(formData.excludeHolidays) &&
          formData.excludeHolidays.length > 0 && { excludeHolidays: formData.excludeHolidays }),
        scheduleConfig: buildScheduleConfigPayload(),
      });

      const payload = unwrapApiPayload(result);
      if (payload) {
        setPreviewResults(payload);

        const actionableWarnings = getActionableTimetableWarnings(payload.report);
        if (!payload.isFeasible) {
          setSnackbar({
            open: true,
            message:
              payload.report?.errors?.[0]?.message ||
              "Timetable cannot be generated with the current configuration.",
            severity: "error",
          });
        } else {
          setActiveStep((prev) => prev + 1);
          if (actionableWarnings.length > 0) {
            setSnackbar({
              open: true,
              message: actionableWarnings[0].message,
              severity: "warning",
            });
          } else {
            setSnackbar({
              open: true,
              message: "Timetable generation is feasible!",
              severity: "success",
            });
          }
        }
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Failed to check feasibility",
        severity: 'error',
      });
    }
  };

  const handleCreateFromScratch = async () => {
    try {
      const result = await createFromScratchMutation.mutateAsync({
        name: `Timetable ${formData.academicYear}`,
        academicYear: formData.academicYear,
        instituteId: resolveWizardInstituteId(),
        departmentId: formData.departmentId,
        scheduleConfig: buildScheduleConfigPayload(),
      });
      const timetableId = result?.data?.data?.id ?? result?.data?.id;
      if (timetableId) {
        toast.success("Empty timetable created. Add slots in the Kanban editor.");
        navigate(`${rolePath}/timetable/edit/${timetableId}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to create timetable");
    }
  };

  const handleGenerate = async () => {
    try {
      const result = await generateMutation.mutateAsync({
        academicYear: formData.academicYear,
        instituteId: resolveWizardInstituteId(),
        departmentId: formData.departmentId,
        classIds: formData.classId ? [formData.classId] : undefined,
        ...buildSubjectIdsPayload(formData.subjectIds),
        generationMode: formData.generationMode,
        excludeWeekends: formData.excludeWeekends,
        ...(Array.isArray(formData.excludeHolidays) &&
          formData.excludeHolidays.length > 0 && { excludeHolidays: formData.excludeHolidays }),
        scheduleConfig: buildScheduleConfigPayload(),
      });
      console.log("Generation result:", result);
      console.log("Generation result data:", result.data);

      const payload = unwrapApiPayload(result);
      if (payload) {
        // Check if generation failed due to missing subjects
        if (!payload.success && payload.reason === 'no_subjects') {
          setSnackbar({
            open: true,
            message: 'No subjects assigned to classes! Please assign subjects to classes first before generating timetable. Go to Subjects page → Click the blue + button on subjects to assign them to classes',
            severity: 'error',
          });
          setActiveStep(0); // Go back to configuration step
          return;
        }

        setSnackbar({
          open: true,
          message: 'Timetable generated successfully!',
          severity: 'success',
        });
        
        // Show success dialog with options
        setGeneratedTimetableId(payload?.timetableId ?? payload?.data?.timetableId);
        setSuccessDialogOpen(true);
      }
    } catch (error) {
      console.error("Generation error:", error);
      
      // Handle structured error response
      if (error.response?.data?.reason === 'infeasible' && error.response?.data?.report) {
        setGenerationError(error.response.data);
        setErrorDialogOpen(true);
      } else {
        const errorMessage = error.response?.data?.message || "Failed to generate timetable";
        setSnackbar({
          open: true,
          message: errorMessage,
          severity: 'error',
        });
      }
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Configure Generation Parameters
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Set the academic parameters for timetable generation
            </Typography>

           
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Generation Mode</InputLabel>
                  <Select
                    value={formData.generationMode}
                    label="Generation Mode"
                    onChange={(e) =>
                      setFormData({ ...formData, generationMode: e.target.value })
                    }
                  >
                    <MenuItem value="fully_automatic">Fully Automatic (AI-based)</MenuItem>
                    <MenuItem value="fully_manual">Fully Manual</MenuItem>
                    <MenuItem value="hybrid">Hybrid (Auto + Manual adjustment)</MenuItem>
                  </Select>
                </FormControl>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                  {formData.generationMode === "fully_manual" && "Create empty timetable and add slots manually"}
                  {formData.generationMode === "hybrid" && "Generate first, then edit in Kanban"}
                  {formData.generationMode === "fully_automatic" && "Generate using genetic algorithm"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required sx={{ position: "relative" }}>
                  <InputLabel>Academic Year</InputLabel>
                  <Select
                    value={formData.academicYear}
                    label="Academic Year"
                    onChange={(e) => {
                      setFormData({ ...formData, academicYear: e.target.value });
                      if (academicYearError) setAcademicYearError(validateRequired(e.target.value, "Academic year"));
                    }}
                    onBlur={() => setAcademicYearError(validateRequired(formData.academicYear, "Academic year"))}
                    error={!!academicYearError}
                    disabled={academicYearsLoading}
                  >
                    {academicYears.map((year) => (
                      <MenuItem key={year.id || year.academicYear} value={year.academicYear}>
                        {year.academicYear}
                      </MenuItem>
                    ))}
                  </Select>
                  {academicYearError && (
                    <FormHelperText error>{academicYearError}</FormHelperText>
                  )}
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                    {academicYearsLoading ? "Loading academic years..." : `${academicYears.length} academic years available`}
                  </Typography>
                  {academicYearsLoading && (
                    <CircularProgress
                      size={18}
                      sx={{ position: "absolute", right: 36, top: 18, pointerEvents: "none" }}
                    />
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Alert severity="info">
                  <Typography variant="body2">
                    <strong>Optional Filters:</strong> Leave filters empty to
                    generate for all departments, classes, and subjects. You can select multiple subjects.
                  </Typography>
                </Alert>
              </Grid>

              {showInstituteSelection && (
                <Grid item xs={12} md={4}>
                  <Autocomplete
                    options={institutes}
                    loading={institutesLoading}
                    disabled={institutesLoading}
                    getOptionLabel={(option) => option.name || ""}
                    value={institutes.find((i) => (i.id || i.instituteId) === formData.instituteId) || null}
                    onChange={(e, newValue) =>
                      setFormData({
                        ...formData,
                        instituteId: newValue ? (newValue.id ?? newValue.instituteId) : null,
                        departmentId: null,
                        classId: null,
                        subjectIds: [],
                      })
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Institute (optional)"
                        helperText={
                          institutesLoading
                            ? "Loading institutes..."
                            : `${institutes.length} institutes available`
                        }
                      />
                    )}
                  />
                </Grid>
              )}

              {showDepartmentSelection && (
                <Grid item xs={12} md={4}>
                  <Autocomplete
                    options={departments}
                    loading={departmentsLoading}
                    disabled={departmentsLoading}
                    getOptionLabel={(option) => option.name || ""}
                    value={departments.find((d) => (d.id || d.departmentId) === formData.departmentId) || null}
                    onChange={(e, newValue) =>
                      setFormData({
                        ...formData,
                        departmentId: newValue ? (newValue.id ?? newValue.departmentId) : null,
                        classId: null,
                        subjectIds: [],
                      })
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Department (optional)"
                        helperText={
                          departmentsLoading
                            ? "Loading departments..."
                            : `${departments.length} departments available`
                        }
                      />
                    )}
                  />
                </Grid>
              )}

              <Grid item xs={12} md={showInstituteSelection ? 4 : 6}>
                <Autocomplete
                  options={filteredClasses}
                  loading={classesLoading}
                  disabled={classesLoading}
                  getOptionLabel={(option) => {
                    const name = option.name || option.className || "";
                    const section = option.section || option.classSection || "";
                    return section ? `${name} - ${section}` : name;
                  }}
                  value={
                    filteredClasses.find(
                      (c) =>
                        String(c.id ?? c.classId) === String(formData.classId ?? "")
                    ) || null
                  }
                  onChange={(e, newValue) =>
                    setFormData({
                      ...formData,
                      classId: newValue ? (newValue.id ?? newValue.classId) : null,
                      subjectIds: [], // Reset subjects when class changes
                    })
                  }
                  renderInput={(params) => (
                    <TextField 
                      {...params} 
                      label="Class (optional)" 
                      helperText={
                        classesLoading
                          ? "Loading classes..."
                          : `${filteredClasses.length} classes available`
                      }
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={showInstituteSelection ? 4 : 6}>
                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  options={filteredSubjects}
                  loading={subjectsDropdownLoading}
                  disabled={subjectsDropdownLoading}
                  getOptionLabel={(option) => option.name || option.subjectName || ""}
                  isOptionEqualToValue={(a, b) =>
                    String(getSubjectOptionId(a)) === String(getSubjectOptionId(b))
                  }
                  value={filteredSubjects.filter((s) => {
                    const id = getSubjectOptionId(s);
                    return (
                      id != null &&
                      (formData.subjectIds || []).some((selectedId) => Number(selectedId) === id)
                    );
                  })}
                  onChange={(e, newValue) =>
                    setFormData({
                      ...formData,
                      subjectIds: (newValue || [])
                        .map(getSubjectOptionId)
                        .filter((id) => id != null),
                    })
                  }
                  renderOption={(props, option, { selected }) => (
                    <li {...props} key={String(getSubjectOptionId(option))}>
                      <Checkbox style={{ marginRight: 8 }} checked={selected} />
                      {option.name || option.subjectName || ""}
                    </li>
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={String(getSubjectOptionId(option))}
                        label={option.name || option.subjectName || ""}
                        size="small"
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Subjects (optional)"
                      placeholder={
                        (formData.subjectIds || []).length === 0 ? "Select one or more subjects" : ""
                      }
                      helperText={
                        subjectsDropdownLoading
                          ? "Loading subjects..."
                          : formData.classId
                            ? `${filteredSubjects.length} subjects for this class — leave empty for all`
                            : `${filteredSubjects.length} subjects — leave empty to generate for all subjects`
                      }
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" gutterBottom>
                      School day &amp; breaks
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Defines period length and non-teaching blocks (lunch, tea, etc.). Auto-generation only assigns
                      classes to teaching periods; breaks stay free. Manual timetables use the same grid in the editor.
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          label="Day starts"
                          type="time"
                          InputLabelProps={{ shrink: true }}
                          value={formData.scheduleConfig.dayStart}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              scheduleConfig: { ...formData.scheduleConfig, dayStart: e.target.value },
                            })
                          }
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          label="Day ends"
                          type="time"
                          InputLabelProps={{ shrink: true }}
                          value={formData.scheduleConfig.dayEnd}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              scheduleConfig: { ...formData.scheduleConfig, dayEnd: e.target.value },
                            })
                          }
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          label="Period duration (minutes)"
                          type="number"
                          InputLabelProps={{ shrink: true }}
                          value={formData.scheduleConfig.slotDurationMinutes}
                          onChange={(e) => {
                            const rawValue = e.target.value;
                            
                            // Allow empty input, partial typing, and backspace operations
                            if (rawValue === '' || rawValue === '-' || rawValue === '0' || rawValue === '1' || rawValue === '2' || rawValue === '3' || rawValue === '4' || rawValue === '5' || rawValue === '6' || rawValue === '7' || rawValue === '8' || rawValue === '9') {
                              // Allow single digits, empty, and negative sign while typing
                              setFormData({
                                ...formData,
                                scheduleConfig: {
                                  ...formData.scheduleConfig,
                                  slotDurationMinutes: rawValue,
                                },
                              });
                              return;
                            }
                            
                            // For multi-digit inputs, validate and apply range
                            const parsedValue = parseInt(rawValue);
                            if (!isNaN(parsedValue)) {
                              setFormData({
                                ...formData,
                                scheduleConfig: {
                                  ...formData.scheduleConfig,
                                  slotDurationMinutes: Math.max(1, Math.min(600, parsedValue)),
                                },
                              });
                            }
                          }}
                          onBlur={(e) => {
                            // Final validation on blur
                            const value = parseInt(e.target.value) || 60;
                            setFormData({
                              ...formData,
                              scheduleConfig: {
                                ...formData.scheduleConfig,
                                slotDurationMinutes: Math.max(1, Math.min(600, value)),
                              },
                            });
                          }}
                          inputProps={{ 
                            min: 1, 
                            max: 600,
                            step: 1,
                            pattern: '[0-9]*'
                          }}
                          helperText="Enter period duration in minutes (e.g. 5, 10, 45, 100)"
                          placeholder="60"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                          Breaks (overlapping periods are marked as break — no teaching)
                        </Typography>
                        {(formData.scheduleConfig.breaks || []).map((br, bi) => (
                          <Box
                            key={bi}
                            sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center", mb: 1 }}
                          >
                            <TextField
                              size="small"
                              label="Name"
                              value={br.label || ""}
                              onChange={(e) => {
                                const next = [...(formData.scheduleConfig.breaks || [])];
                                next[bi] = { ...next[bi], label: e.target.value };
                                setFormData({
                                  ...formData,
                                  scheduleConfig: { ...formData.scheduleConfig, breaks: next },
                                });
                              }}
                              sx={{ minWidth: 140 }}
                            />
                            <TextField
                              size="small"
                              label="From"
                              type="time"
                              InputLabelProps={{ shrink: true }}
                              value={br.start || ""}
                              onChange={(e) => {
                                const next = [...(formData.scheduleConfig.breaks || [])];
                                next[bi] = { ...next[bi], start: e.target.value };
                                setFormData({
                                  ...formData,
                                  scheduleConfig: { ...formData.scheduleConfig, breaks: next },
                                });
                              }}
                            />
                            <TextField
                              size="small"
                              label="To"
                              type="time"
                              InputLabelProps={{ shrink: true }}
                              value={br.end || ""}
                              onChange={(e) => {
                                const next = [...(formData.scheduleConfig.breaks || [])];
                                next[bi] = { ...next[bi], end: e.target.value };
                                setFormData({
                                  ...formData,
                                  scheduleConfig: { ...formData.scheduleConfig, breaks: next },
                                });
                              }}
                            />
                            <IconButton
                              size="small"
                              aria-label="Remove break"
                              onClick={() => {
                                const next = (formData.scheduleConfig.breaks || []).filter((_, i) => i !== bi);
                                setFormData({
                                  ...formData,
                                  scheduleConfig: {
                                    ...formData.scheduleConfig,
                                    breaks: next.length > 0 ? next : [{ start: "12:00", end: "13:00", label: "Lunch" }],
                                  },
                                });
                              }}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Box>
                        ))}
                        <Button
                          size="small"
                          startIcon={<Add />}
                          onClick={() =>
                            setFormData({
                              ...formData,
                              scheduleConfig: {
                                ...formData.scheduleConfig,
                                breaks: [
                                  ...(formData.scheduleConfig.breaks || []),
                                  { start: "15:00", end: "15:15", label: "Tea break" },
                                ],
                              },
                            })
                          }
                        >
                          Add break
                        </Button>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" gutterBottom>
                      Schedule Preferences
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <FormControl component="fieldset">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Typography variant="body2">Exclude Sundays</Typography>
                            <Box
                              component="input"
                              type="checkbox"
                              checked={formData.excludeWeekends}
                              onChange={(e) => setFormData({ ...formData, excludeWeekends: e.target.checked })}
                              sx={{ cursor: 'pointer' }}
                            />
                          </Box>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12}>
                        <Alert severity="info" icon={<Warning />}>
                          <Typography variant="caption">
                            <strong>Note:</strong> The system will automatically exclude dates where teachers are on approved leave.
                            Teachers on leave will show "On Leave" in the timetable for those dates.
                          </Typography>
                        </Alert>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" gutterBottom>
                      Generation Algorithm Settings
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          Population Size: <strong>50</strong>
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          Max Generations: <strong>100</strong>
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          Mutation Rate: <strong>15%</strong>
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          Crossover Rate: <strong>70%</strong>
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        );

      case 1:
        if (formData.generationMode === "fully_manual") {
          return (
            <Box>
              <Typography variant="h6" gutterBottom>
                Create Empty Timetable
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                An empty timetable will be created using your school day and break settings from step 1. Add teaching
                slots in the Kanban editor; break periods appear as non-teaching rows.
              </Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                Academic Year: {formData.academicYear} | Institute: {formData.instituteId || currentUser?.instituteId || "Current"}
              </Alert>
            </Box>
          );
        }
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Feasibility Check Results
            </Typography>

            {previewMutation.isPending && (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
                <Box textAlign="center">
                  <CircularProgress size={60} />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    Checking feasibility...
                  </Typography>
                </Box>
              </Box>
            )}

            {!previewMutation.isPending && !previewResults && (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
                <Box textAlign="center">
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                    Ready to check feasibility
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Click the "Check Feasibility" button below to analyze your timetable generation parameters
                  </Typography>
                </Box>
              </Box>
            )}

            {previewResults && (
              <Box>
                {(() => {
                  const actionableWarnings = getActionableTimetableWarnings(previewResults.report);
                  const isFeasible = previewResults.isFeasible === true;
                  const showWarning = !isFeasible || actionableWarnings.length > 0;
                  return (
                <Alert
                  severity={showWarning ? "warning" : "success"}
                  icon={!isFeasible ? <Warning /> : showWarning ? <Warning /> : <CheckCircle />}
                  sx={{ mb: 3 }}
                >
                  <Typography variant="body1" fontWeight={500}>
                    {!isFeasible
                      ? "Timetable cannot be generated with the current configuration"
                      : actionableWarnings.length > 0
                        ? "Timetable generation is feasible with warnings"
                        : "Timetable generation is feasible!"}
                  </Typography>
                  <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                    {!isFeasible
                      ? "Resolve the issues below before continuing"
                      : actionableWarnings.length > 0
                        ? actionableWarnings[0].message
                        : "You can proceed with generation"}
                  </Typography>
                </Alert>
                  );
                })()}

                {previewResults.subjectScopeWarning && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    {previewResults.subjectScopeWarning}
                  </Alert>
                )}

                {previewResults.statistics?.selectedSubjectIds?.length > 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Selected {previewResults.statistics.selectedSubjectIds.length} subject(s) —{" "}
                    {previewResults.statistics.subjectsScheduled ?? 0} will be scheduled
                    {previewResults.statistics.missingSubjectIds?.length > 0
                      ? ` (${previewResults.statistics.missingSubjectIds.length} skipped)`
                      : ""}
                    .
                  </Typography>
                )}

                {previewResults.statistics && (
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                      <Paper sx={{ p: 2, textAlign: "center" }}>
                        <Typography variant="h4" color="primary">
                          {previewResults.statistics.totalClasses || 0}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Classes
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Paper sx={{ p: 2, textAlign: "center" }}>
                        <Typography variant="h4" color="primary">
                          {previewResults.statistics.totalTeachers || 0}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Teachers
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Paper sx={{ p: 2, textAlign: "center" }}>
                        <Typography variant="h4" color="primary">
                          {previewResults.statistics.totalSubjects || 0}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Subjects
                        </Typography>
                      </Paper>
                    </Grid>
                    {previewResults.statistics.totalRequiredHours != null && (
                      <Grid item xs={12}>
                        <Paper sx={{ p: 2 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            Weekly Workload vs Available Slots
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Required lecture hours (from subject weekly hours):{" "}
                            <strong>{previewResults.statistics.totalRequiredHours}</strong>
                            {" · "}
                            Available teaching slots:{" "}
                            <strong>{previewResults.statistics.availableSlots ?? "—"}</strong>
                          </Typography>
                          {previewResults.statistics.totalRequiredHours >
                            (previewResults.statistics.availableSlots || 0) && (
                            <Alert severity="warning" sx={{ mt: 1 }}>
                              Required weekly hours exceed available slots. Generation may not satisfy all
                              subject hour targets.
                            </Alert>
                          )}
                        </Paper>
                      </Grid>
                    )}
                  </Grid>
                )}

                {previewResults.report && previewResults.report.errors && previewResults.report.errors.length > 0 && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Issues Detected:
                    </Typography>
                    <List>
                      {previewResults.report.errors.map((issue, index) => (
                        <React.Fragment key={index}>
                          <ListItem>
                            <Error color="error" sx={{ mr: 2 }} />
                            <ListItemText
                              primary={issue.message}
                              secondary={issue.type}
                            />
                          </ListItem>
                          {index < previewResults.report.errors.length - 1 && <Divider />}
                        </React.Fragment>
                      ))}
                    </List>
                  </Box>
                )}

                {previewResults.report?.notices?.length > 0 && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Notes:
                    </Typography>
                    <List>
                      {previewResults.report.notices.map((notice, index) => (
                        <React.Fragment key={`notice-${index}`}>
                          <ListItem>
                            <Warning color="info" sx={{ mr: 2 }} />
                            <ListItemText primary={notice.message} />
                          </ListItem>
                          {index < previewResults.report.notices.length - 1 && <Divider />}
                        </React.Fragment>
                      ))}
                    </List>
                  </Box>
                )}

                {previewResults.report &&
                  getActionableTimetableWarnings(previewResults.report).length > 0 && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Warnings:
                    </Typography>
                    <List>
                      {getActionableTimetableWarnings(previewResults.report).map((warning, index, arr) => (
                        <React.Fragment key={index}>
                          <ListItem>
                            <Warning color="warning" sx={{ mr: 2 }} />
                            <ListItemText primary={warning.message} />
                          </ListItem>
                          {index < arr.length - 1 && <Divider />}
                        </React.Fragment>
                      ))}
                    </List>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Generate Timetable
            </Typography>

            {generateMutation.isPending && (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
                <Box textAlign="center">
                  <CircularProgress size={60} />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    Generating timetable using Genetic Algorithm...
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    This may take a few moments
                  </Typography>
                </Box>
              </Box>
            )}

            {generateMutation.isSuccess && (
              <Alert severity="success" icon={<CheckCircle />}>
                <Typography variant="body1" fontWeight={500}>
                  Timetable Generated Successfully!
                </Typography>
                <Typography variant="caption">
                  Redirecting to timetable view...
                </Typography>
              </Alert>
            )}

            {!generateMutation.isPending && !generateMutation.isSuccess && (
              <Alert severity="info">
                <Typography variant="body1">
                  Click "Generate" to start the generation process
                </Typography>
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                  The Genetic Algorithm will optimize the timetable based on:
                </Typography>
                <Box component="ul" sx={{ mt: 1, pl: 2 }}>
                  <li>Teacher availability</li>
                  <li>Room constraints</li>
                  <li>Class schedules</li>
                  <li>Subject requirements</li>
                  <li>Workload distribution</li>
                </Box>
              </Alert>
            )}
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={600}>
          Timetable Generation Wizard
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Generate optimized timetables using Genetic Algorithm
        </Typography>
      </Box>

      {/* Stepper */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stepper activeStep={activeStep}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </CardContent>
      </Card>

      {/* Step Content */}
      <Card>
        <CardContent sx={{ minHeight: 400 }}>
          {renderStepContent(activeStep)}
        </CardContent>

        <Divider />

        {/* Navigation Buttons */}
        <Box sx={{ p: 2, display: "flex", justifyContent: "space-between" }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={handleBack}
            disabled={activeStep === 0 || generateMutation.isPending}
          >
            Back
          </Button>

          {activeStep < steps.length - 1 ? (
            <Button
              variant="contained"
              endIcon={<ArrowForward />}
              onClick={handleNext}
              disabled={
                previewMutation.isPending ||
                (activeStep === 2 &&
                  formData.generationMode !== "fully_manual" &&
                  previewResults &&
                  !previewResults.isFeasible) ||
                (formData.generationMode === "fully_manual" && activeStep === 1 && createFromScratchMutation.isPending)
              }
            >
              {formData.generationMode === "fully_manual" && activeStep === 1
                ? "Create"
                : activeStep === 1
                ? "Check Feasibility"
                : "Next"}
            </Button>
          ) : (
            <Button
              variant="contained"
              color="primary"
              startIcon={<Star />}
              onClick={
                formData.generationMode === "fully_manual" ? handleCreateFromScratch : handleGenerate
              }
              disabled={
                generateMutation.isPending ||
                generateMutation.isSuccess ||
                (formData.generationMode !== "fully_manual" &&
                  previewResults &&
                  !previewResults.isFeasible) ||
                (formData.generationMode === "fully_manual" && createFromScratchMutation.isPending)
              }
            >
              {formData.generationMode === "fully_manual" ? "Create Empty Timetable" : "Generate Timetable"}
            </Button>
          )}
        </Box>
      </Card>

      {/* Success Dialog */}
      <Dialog open={successDialogOpen} onClose={(e, reason) => { if (reason === "backdropClick" || reason === "escapeKeyDown") return; setSuccessDialogOpen(false); }} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircle color="success" />
            <Typography variant="h6">Timetable Generated Successfully!</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Your timetable has been generated successfully with ID: <strong>{generatedTimetableId}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            What would you like to do next?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setSuccessDialogOpen(false);
              navigate(`${rolePath}/timetable`);
            }}
          >
            View All Timetables
          </Button>
          <Button 
            variant="contained" 
            onClick={() => {
              setSuccessDialogOpen(false);
              if (!generatedTimetableId) {
                toast.error("Timetable ID missing — open it from Timetable Dashboard.");
                navigate(`${rolePath}/timetable`);
                return;
              }
              navigate(`${rolePath}/timetable/view/${generatedTimetableId}`);
            }}
          >
            View This Timetable
          </Button>
        </DialogActions>
      </Dialog>

      {/* Error Dialog */}
      <Dialog open={errorDialogOpen} onClose={(e, reason) => { if (reason === "backdropClick" || reason === "escapeKeyDown") return; setErrorDialogOpen(false); }} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Error color="error" />
            <Typography variant="h6">Timetable Generation Failed</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {generationError && (
            <Box>
              <Alert severity="error" sx={{ mb: 3 }}>
                <Typography variant="body1" fontWeight={500}>
                  {generationError.message}
                </Typography>
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                  Reason: {generationError.reason}
                </Typography>
              </Alert>

              {generationError.report?.errors && generationError.report.errors.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" gutterBottom color="error">
                    Critical Issues:
                  </Typography>
                  <List>
                    {generationError.report.errors.map((error, index) => (
                      <React.Fragment key={index}>
                        <ListItem>
                          <Error color="error" sx={{ mr: 2 }} />
                          <ListItemText
                            primary={error.message}
                            secondary={error.type}
                          />
                        </ListItem>
                        {index < generationError.report.errors.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                </Box>
              )}

              {generationError.report &&
                getActionableTimetableWarnings(generationError.report).length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" gutterBottom color="warning.main">
                    Warnings:
                  </Typography>
                  <List>
                    {getActionableTimetableWarnings(generationError.report).map((warning, index, arr) => (
                      <React.Fragment key={index}>
                        <ListItem>
                          <Warning color="warning" sx={{ mr: 2 }} />
                          <ListItemText primary={warning.message} />
                        </ListItem>
                        {index < arr.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                </Box>
              )}

              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Suggestions:</strong>
                  <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                    <li>Assign more teachers to subjects</li>
                    <li>Update teacher availability schedules</li>
                    <li>Check if subjects are properly assigned to classes</li>
                    <li>Verify department and class configurations</li>
                  </ul>
                </Typography>
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setErrorDialogOpen(false);
              setActiveStep(0); // Go back to configuration step
            }}
          >
            Back to Configuration
          </Button>
          <Button 
            variant="contained" 
            onClick={() => setErrorDialogOpen(false)}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GenerationWizard;
